import { StepperLayout, StepperLayoutStep, useComputed } from "@homebound/beam";
import { useFormState } from "@homebound/form-state";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { BidPackageStatus, ReadyPlanStatus } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { PlanBasedBidPackageDetailsStep } from "src/routes/bids/plan-based-bid-package/components/1.PlanBasedBidPackageDetailsStep";
import { ConfigureBidGroupsStep } from "src/routes/bids/plan-based-bid-package/components/2.ConfigureBidGroupsStep";
import { ReviewAndDownloadStep } from "src/routes/bids/plan-based-bid-package/components/3.ReviewAndDownloadStep";
import {
  PlanBasedBidPackagePageQuery,
  PlanBasedBidPackagePage_BidPackageFragment,
  PlanBasedBidPackagePage_OriginatingProjectFragment,
  usePlanBasedBidPackagePageQuery,
  usePlanBasedBidPackagePage_OriginatingProjectQuery,
} from "src/routes/bids/plan-based-bid-package/PlanBasedBidPackagePage.generated";
import { useSavePlanBasedBidPackage } from "src/routes/bids/plan-based-bid-package/useSavePlanBasedBidPackage";
import {
  BaseBidType,
  INTERNAL_ESTIMATE_ID,
  PlanBasedBidPackageBidPlanGroup,
  PlanBasedBidPackageFormState,
  PlanBasedBidPackageStep,
  PlanBasedBidPackageWizard,
  hasBidGroups,
  hasUnsavedPackageDetails,
  isBaseGroup,
  isCoreReady,
  parseCostType,
  planBasedBidPackageFormConfig,
} from "src/routes/bids/plan-based-bid-package/utils";
import { BidPackageParams } from "src/routes/routesDef";
import { createBidPackagesDetailUrl, createBidPackagesUrl } from "src/RouteUrls";
import { fail, formatList, queryResult } from "src/utils";
import { ArrayParam, StringParam, useQueryParams } from "use-query-params";

export function PlanBasedBidPackagePage() {
  const { bidPackageGroupId } = useRouteParams<BidPackageParams>();
  const [{ projectId }] = useQueryParams({ projectId: StringParam });
  const query = usePlanBasedBidPackagePageQuery({
    variables: { id: bidPackageGroupId || "" },
    skip: !bidPackageGroupId,
  });
  const originatingProjectQuery = usePlanBasedBidPackagePage_OriginatingProjectQuery({
    variables: { id: projectId! },
    skip: !!bidPackageGroupId || !projectId,
  });

  if (!bidPackageGroupId) {
    return projectId ? (
      queryResult(originatingProjectQuery, ({ project }) => (
        <PlanBasedBidPackageView isEdit={false} originatingProject={project} />
      ))
    ) : (
      <PlanBasedBidPackageView isEdit={false} />
    );
  }

  return queryResult(query, ({ bidPackageGroup }) => (
    <PlanBasedBidPackageView
      isEdit={bidPackageGroup.status.code !== BidPackageStatus.Draft}
      bidPackageGroup={bidPackageGroup}
    />
  ));
}

type PlanBasedBidPackageViewProps = {
  isEdit: boolean;
  bidPackageGroup?: PlanBasedBidPackagePageQuery["bidPackageGroup"];
  originatingProject?: PlanBasedBidPackagePage_OriginatingProjectFragment;
};

function PlanBasedBidPackageView(props: PlanBasedBidPackageViewProps) {
  const { isEdit, bidPackageGroup, originatingProject } = props;
  const isCreate = !isEdit;
  const [{ projectId, costCodes: costCodesParam, costType: costTypeParam }] = useQueryParams({
    projectId: StringParam,
    costCodes: ArrayParam,
    costType: StringParam,
  });
  const costCodes = costCodesParam?.compact() ?? [];
  const costType = parseCostType(costTypeParam);
  const navigate = useNavigate();

  const formState = useFormState({
    config: planBasedBidPackageFormConfig,
    init: {
      input: bidPackageGroup?.bidPackages.first,
      map: (bidPackage) => mapBidPackageToForm(bidPackage),
      ifUndefined: {
        marketId: originatingProject?.market?.id,
        projects: originatingProject ? [originatingProject.id] : [],
        // If we're coming from a community project, then pre-select all active product offerings
        productOfferings:
          originatingProject && "productOfferings" in originatingProject
            ? originatingProject.productOfferings
                .filter((po) => po.status.code === ReadyPlanStatus.Active)
                .map((po) => po.id)
            : [],
        costCodes: costCodes.nonEmpty ? costCodes : undefined,
        costType,
        bidPlanGroups: [{ order: 0, clientId: "base:1", dimensions: [] }],
        baseBidType: BaseBidType.Standard,
      },
    },
  });

  // `saving` feeds each step's `primaryDisabled` to disable Continue/Complete: Beam's `onContinue`
  // has no in-flight guard, so otherwise a double-click creates two bid packages.
  const { persist, persistWithCostRemovalConfirmation, saveBidPackages, saving } =
    useSavePlanBasedBidPackage(formState);

  // Persists the form (after confirming costed-line removal when needed). `exiting` navigates away
  // (Save & Exit / Save); Continue passes `false` and lets `StepperLayout` advance instead.
  const save = useCallback(
    async (exiting: boolean) => {
      const saved = await persistWithCostRemovalConfirmation();
      // The user declined to remove costed bid lines, so stay put with the form still dirty
      if (!saved) return undefined;
      if (exiting) {
        const groupId = saved.bidPackageGroup?.id;
        const url = isEdit ? createBidPackagesDetailUrl(groupId!, saved.id) : createBidPackagesUrl(projectId);
        navigate(url);
      }
      return saved;
    },
    [persistWithCostRemovalConfirmation, projectId, isEdit, navigate],
  );

  // Void-returning wrapper for StepperLayout press handlers (`onComplete` / `onSaveAndExit`)
  const saveAndExit = useCallback(async () => {
    await save(true);
  }, [save]);

  // The Review step's "missing options" card stages options into this same form rather than saving
  // its own exhaustive `bidPlanGroups` list behind our back, which would leave this form stale.
  const wizard = useMemo<PlanBasedBidPackageWizard>(() => ({ formState, save: () => save(false) }), [formState, save]);

  // Saving is what advances the workflow. `StepperLayout` runs this before the next step and treats
  // `false` as "stay put".
  const onContinue = useCallback(async () => {
    try {
      const saved = await save(false);
      if (!saved) return false;
    } catch {
      // Leave the user on this step so they can retry.
      return false;
    }
  }, [save]);

  const submitBidPackage = useCallback(async () => {
    const saved = await persist();
    const bidPackageId =
      saved?.id ?? formState.value.bidPackageId ?? fail("Cannot submit a bid package that has not been saved");
    const groupId =
      saved?.bidPackageGroup?.id ??
      formState.value.bidPackageGroupId ??
      fail("Cannot submit a bid package that has not been saved");
    await saveBidPackages({ variables: { input: [{ id: bidPackageId, status: BidPackageStatus.Open }] } });
    navigate(createBidPackagesDetailUrl(groupId, bidPackageId));
  }, [formState, persist, saveBidPackages, navigate]);

  // Runs *after* `StepperLayout`'s `isDirty` prompt, which owns the discard-changes confirmation.
  const onCancel = useCallback(() => {
    // Same destination "Save & Exit" uses, but without saving — a never-saved create draft has no
    // ids yet, so it falls back to the list url.
    const { bidPackageGroupId, bidPackageId } = formState.value;
    const url =
      bidPackageGroupId && bidPackageId
        ? createBidPackagesDetailUrl(bidPackageGroupId, bidPackageId)
        : createBidPackagesUrl(projectId);
    // Beam's guard is two-part: the Cancel prompt *and* a router `useBlocker` on the same `isDirty`.
    // Navigating still-dirty would stack a second "Leave page?" modal on the one just confirmed.
    formState.revertChanges();
    navigate(url);
  }, [formState, navigate, projectId]);

  // Flat primitives so `useComputed`'s `shallowEqual` bailout can fire: the array-valued reads below
  // change identity on every edit, even when no gate moved.
  const gating = useComputed(
    () => ({
      coreReady: isCoreReady(formState),
      hasGroups: hasBidGroups(formState),
      // Both later steps read the *saved* bid package, but Beam's tabs advance on click without
      // running `onContinue` — so gate them on that data being persisted, not just on form validity.
      saved: !!formState.bidPackageId.value,
      detailsUnsaved: hasUnsavedPackageDetails(formState),
      // Whole-form `dirty`: the review table also reflects bid groups and bidders, and that step has
      // no fields of its own, so it can't disable the tab you're on.
      dirty: formState.dirty,
      valid: formState.valid,
      // Tooltip copy for step 2's Continue (same rule as `hasBidGroups`).
      bidPlanGroupsError: formatList(formState.bidPlanGroups.errors) || undefined,
    }),
    [formState],
  );

  // `saving` is Apollo/React state rather than an observable, so it belongs here and not in the autorun.
  const steps = useMemo<StepperLayoutStep[]>(() => {
    const { coreReady, hasGroups, saved, detailsUnsaved, dirty, valid, bidPlanGroupsError } = gating;
    return [
      {
        label: PlanBasedBidPackageStep.DEFINE_PACKAGE,
        disabled: false,
        primaryDisabled: saving ? true : !coreReady ? "Fill all required fields to continue." : false,
        onContinue,
        content: <PlanBasedBidPackageDetailsStep formState={formState} originatingProject={originatingProject} />,
      },
      {
        label: PlanBasedBidPackageStep.CONFIGURE_BID_GROUPS,
        disabled: !coreReady || !saved || detailsUnsaved,
        primaryDisabled: saving ? true : !(coreReady && hasGroups) ? (bidPlanGroupsError ?? true) : false,
        onContinue,
        content: <ConfigureBidGroupsStep formState={formState} />,
      },
      {
        label: PlanBasedBidPackageStep.REVIEW_AND_DOWNLOAD,
        disabled: !coreReady || !hasGroups || !saved || dirty,
        primaryDisabled: saving || !valid,
        content: <ReviewAndDownloadStep formState={formState} wizard={wizard} />,
      },
    ];
  }, [gating, saving, formState, originatingProject, onContinue, wizard]);

  return (
    <StepperLayout
      title={`${isEdit ? "Edit" : "Create"} Plan-based Bid`}
      completeLabel={isCreate ? "Create" : "Save"}
      onCancel={onCancel}
      onComplete={isCreate ? submitBidPackage : saveAndExit}
      onSaveAndExit={saveAndExit}
      isDirty={() => formState.dirty}
      steps={steps}
    />
  );
}

export function mapBidPackageToForm(
  bidPackage: PlanBasedBidPackagePage_BidPackageFragment,
): PlanBasedBidPackageFormState {
  const internalEstimate = bidPackage.internalEstimateBidContract;
  const hasInternalEstimate = internalEstimate && !internalEstimate.archivedAt;
  const bidPlanGroups = bidPackage.bidPlanGroups.map((bpg) => ({
    id: bpg.id,
    isNoCost: bpg.isNoCost,
    globalOptionId: bpg.globalOption?.id,
    primaryOptionGroupId: bpg.primaryOptionGroup?.id,
    order: bpg.order,
    archive: false,
    dimensions: bpg.dimensions.map((d) => ({
      id: d.id,
      // A dimension is either type-based or group-based (personalization), never both.
      globalOptionTypeId: d.globalOptionType?.id,
      globalOptionGroupId: d.globalOptionGroup?.id,
      globalOptionIds: d.globalOptions.map((go) => go.id),
    })),
  }));
  return {
    bidPackageGroupId: bidPackage.bidPackageGroup?.id,
    bidPackageId: bidPackage.id,
    marketId: bidPackage.projects.first?.market?.id,
    projects: bidPackage.projects.map((p) => p.id),
    costCodes: bidPackage.costCodes.map((cc) => cc.id),
    costType: bidPackage.costType.code,
    internalEstimateType: hasInternalEstimate ? internalEstimate.internalEstimateType?.code : undefined,
    productOfferings: bidPackage.productOfferings.map((po) => po.id),
    requests: [
      ...(hasInternalEstimate ? [{ id: internalEstimate.id, tradePartnerId: INTERNAL_ESTIMATE_ID }] : []),
      ...bidPackage.requests.map((r) => ({ id: r.id, tradePartnerId: r.tradePartner.id, archive: !!r.archivedAt })),
    ],
    bidPlanGroups,
    baseBidType: deriveBaseBidType(bidPlanGroups),
  };
}

/** Derives the base bid mode from the mapped groups. */
function deriveBaseBidType(groups: PlanBasedBidPackageBidPlanGroup[]): BaseBidType {
  const baseGroups = groups.filter(isBaseGroup);
  const hasPersonalization = baseGroups.some(
    (g) => g.primaryOptionGroupId || (g.dimensions ?? []).some((d) => d.globalOptionGroupId),
  );
  if (hasPersonalization) return BaseBidType.Personalization;
  return baseGroups.nonEmpty ? BaseBidType.Standard : BaseBidType.None;
}
